<template>
  <div>
    <!-- Hero -->
    <section class="nerv-section hero">
      <div class="nerv-bezel">
        <div class="nerv-screen">
          <div class="hero-grid">
            <div class="hero-main">
              <div class="hero-mark">
                <Logo :pixelSize="heroPixel" />
              </div>
              <h1 class="nerv-heading nerv-heading-lg hero-headline">
                Production LLM and agent systems, built end to end
              </h1>
              <p class="nerv-lede hero-lede">
                Eleven years shipping multi-tenant platforms across .NET, TypeScript and Go,
                now pointed at applied AI.
              </p>
              <div class="hero-actions">
                <router-link to="/contact" class="nerv-btn">Send a project brief</router-link>
                <router-link to="/about" class="nerv-btn nerv-btn-ghost">Hiring? See the service record</router-link>
              </div>
            </div>

            <!-- Operator card: turns the dead right half into the console conceit doing work. -->
            <aside class="operator">
              <span class="nerv-data-label">Operator</span>
              <dl>
                <div v-for="row in operator" :key="row.key" class="nerv-datarow">
                  <dt class="nerv-datarow-key">{{ row.key }}</dt>
                  <dd class="nerv-datarow-value">{{ row.value }}</dd>
                </div>
              </dl>
              <span class="nerv-status nerv-lcd operator-status">Available for engagements</span>
            </aside>
          </div>

          <div class="hero-strip">
            <span class="nerv-caption">Remote and on-site</span>
            <span class="nerv-caption">1099 through Dev1 Consulting LLC</span>
            <span class="nerv-caption">Texas LLC, active and in good standing</span>
          </div>
        </div>
      </div>

      <!-- Oversized readouts, the "370 km/h" pattern. -->
      <div class="hero-readouts">
        <NervReadout label="Shipping since" value="2015" note="Production .NET, cloud and applied AI" />
        <NervReadout label="Platform end users" value="17" unit="M+" tone="cyan" note="Regulated digital banking platform" />
        <NervReadout label="npm downloads" value="1,900" unit="+" tone="magenta" note="claude-rpg, past 12 months" />
      </div>
    </section>

    <!-- About -->
    <section class="nerv-section">
      <span class="nerv-section-label">Sec 01.1 // About</span>
      <div class="nerv-panel nerv-bracket section-body">
        <h2 class="nerv-heading nerv-heading-lg">About me</h2>
        <p class="nerv-body">
          I build AI systems that survive contact with production. That means the parts most
          demos skip: tool calling that respects who the user is, evals that catch regressions
          before customers do, and multi-tenant boundaries that hold. Eleven years in fintech and
          enterprise platforms taught me that the hard part is rarely the model.
        </p>
        <p class="nerv-body">
          I work through Dev1 Consulting, my own LLC, either building the product or standing up
          an AI initiative inside a team that has not run one before.
        </p>
      </div>
    </section>

    <!-- Services -->
    <section class="nerv-section">
      <span class="nerv-section-label">Sec 01.2 // Capability</span>
      <h2 class="nerv-heading nerv-heading-lg section-title">How I can help</h2>
      <div class="nerv-grid nerv-grid-3">
        <article v-for="(s, i) in services" :key="s.title" class="service">
          <span class="nerv-data-label">{{ pad(i + 1) }}</span>
          <h3 class="nerv-heading nerv-heading-md">{{ s.title }}</h3>
          <p class="nerv-body-sm">{{ s.description }}</p>
        </article>
      </div>
    </section>

    <!-- Tooling -->
    <section class="nerv-section">
      <span class="nerv-section-label">Sec 01.3 // Tooling</span>
      <h2 class="nerv-heading nerv-heading-lg section-title">Tools I work with</h2>
      <ul class="chips">
        <li v-for="tool in tools" :key="tool" class="chip">{{ tool }}</li>
      </ul>
    </section>

    <!-- Work -->
    <section class="nerv-section">
      <span class="nerv-section-label">Sec 01.4 // Record</span>
      <h2 class="nerv-heading nerv-heading-lg section-title">Selected work</h2>
      <div class="nerv-grid nerv-grid-3">
        <article v-for="(p, i) in projects" :key="p.title" class="service">
          <span class="nerv-data-label">{{ p.kind }} {{ pad(i + 1) }}</span>
          <h3 class="nerv-heading nerv-heading-md">{{ p.title }}</h3>
          <p class="nerv-body-sm">{{ p.description }}</p>
          <p v-if="p.stack" class="nerv-caption stack-line">{{ p.stack }}</p>
          <a v-if="p.href" :href="p.href" target="_blank" rel="noopener" class="work-link">
            {{ p.linkLabel }}
          </a>
        </article>
      </div>
    </section>

    <!-- Live command console. It sits after the work rather than above it: it
         is a craft demo and carries no information about Daniil, so it should
         not stand between a reader and the record. -->
    <section class="nerv-section">
      <NervConsole />
    </section>

    <!-- CTA -->
    <section class="nerv-section">
      <div class="nerv-panel nerv-bracket cta">
        <div>
          <h2 class="nerv-heading nerv-heading-lg">Ship an agent system that survives production</h2>
          <p class="nerv-body">
            Tell me what you are trying to build and you get an honest read back: whether it is a
            fit, rough scope, and whether you need me at all. If headcount is the blocker, I can
            start as a contractor through my own LLC.
          </p>
        </div>
        <router-link to="/contact" class="nerv-btn">Send the brief</router-link>
      </div>

      <!-- Verifiable proof, not testimonials. Every item is public and linked. -->
      <div class="proof-strip">
        <span class="nerv-data-label">Live and public</span>
        <ul>
          <li><a href="https://donatotomato.com" target="_blank" rel="noopener">donatotomato.com</a></li>
          <li><a href="https://osrsmarket.net" target="_blank" rel="noopener">osrsmarket.net</a></li>
          <li><a href="https://www.npmjs.com/package/claude-rpg" target="_blank" rel="noopener">npm: claude-rpg</a></li>
          <li><a href="https://wordpress.org/plugins/search/donatotomato/" target="_blank" rel="noopener">WordPress.org plugin</a></li>
          <li><a href="https://github.com/iCodeWebApps" target="_blank" rel="noopener">github.com/iCodeWebApps</a></li>
        </ul>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import Logo from '@/components/Logo.vue';
import NervReadout from '@/components/NervReadout.vue';
import NervConsole from '@/components/NervConsole.vue';

export default defineComponent({
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Home',
  components: { Logo, NervReadout, NervConsole },
  data() {
    return {
      heroPixel: 9,
      operator: [
        { key: 'Name', value: 'Daniil Demidov' },
        { key: 'Focus', value: 'LLM and agent systems' },
        { key: 'Base', value: 'Plano, Texas' },
        { key: 'Entity', value: 'Dev1 Consulting LLC' },
        { key: 'Since', value: '2015' },
      ],
      services: [
        {
          title: 'LLM and agent products',
          description:
            'Prompt design, tool calling, retrieval and evals, built as a product rather than a prototype. Shipped end to end, including the deployment and the observability.',
        },
        {
          title: 'Agent infrastructure',
          description:
            'MCP servers, sub-agent orchestration and agent telemetry. Identity-aware tool use so an agent acts on behalf of a specific user with the right scopes.',
        },
        {
          title: 'AI-powered automation',
          description:
            'Systems that summarize, surface insight and automate multi-step workflows, grounded in your real data and your existing infrastructure rather than a greenfield rewrite.',
        },
        {
          title: 'Multi-tenant SaaS',
          description:
            'Row-level isolation, tenant-safe migrations, MFA and payment flows. The boring, load-bearing parts of a platform that serves more than one customer.',
        },
        {
          title: 'Enterprise integration',
          description:
            'Microsoft Graph, Workday, Freshservice, Nylas, banking cores and CRM systems, connected over queues on an eventual-consistency model that tolerates partial failure.',
        },
        {
          title: 'Auth and security',
          description:
            'OAuth2, OIDC, SAML, TOTP and provider-based authentication, carried over from Fraud and Security work at a regulated digital banking platform.',
        },
      ],
      tools: [
        'Anthropic Claude API', 'OpenAI API', 'MCP', 'TypeScript', 'C# / .NET', 'Go',
        'Python', 'Node.js', 'Vue', 'PostgreSQL', 'SQL Server', 'Supabase',
        'Azure', 'AWS', 'Terraform', 'Stripe Connect',
      ],
      projects: [
        {
          kind: 'Product',
          title: 'DonatoTomato',
          description:
            'Multi-tenant nonprofit fundraising platform, live in production. A second pass at a category I first built at Foundant: after private equity sunset NonProfitCore, I rebuilt the donation platform solo. Stripe Connect direct-charge, row-level isolation, TOTP MFA, nightly reconciliation, companion plugin on WordPress.org.',
          stack: 'Supabase / Stripe Connect / TypeScript',
          href: 'https://donatotomato.com',
          linkLabel: 'donatotomato.com',
        },
        {
          kind: 'Open source',
          title: 'ClaudeRPG',
          description:
            'Real-time visualizer for multi-agent Claude Code sessions. A session-log watcher streams agent telemetry over WebSocket to a rendered client. Published on npm.',
          stack: 'TypeScript / Node / WebSocket',
          href: 'https://www.npmjs.com/package/claude-rpg',
          linkLabel: 'npm: claude-rpg',
        },
        {
          kind: 'Product',
          title: 'OSRS Market AI',
          description:
            'Live consumer LLM product. Claude with tool calling over real-time market data, prompt evals, and Application Insights observability in production.',
          stack: 'Vue 3 / .NET 8 / Azure',
          href: 'https://osrsmarket.net',
          linkLabel: 'osrsmarket.net',
        },
        {
          kind: 'Platform',
          title: 'Enterprise onboarding automation',
          description:
            'HTTP-triggered Azure Functions automating employee on and offboarding, saving two to four hours of manual IT work per hire. Microsoft Graph, Exchange, Workday and Freshservice orchestrated through PowerShell Runbooks over queues.',
          stack: 'Azure Functions / Queues / Microsoft Graph',
        },
        {
          kind: 'Platform',
          title: 'Grant management platform',
          description:
            'Cloud-native multi-tenant grant management at Foundant, including the donor-facing donation hub for NonProfitCore, the direct precursor to DonatoTomato. Event-sourced components with RabbitMQ retry mechanisms.',
          stack: 'Vue 2 / Go / MySQL / AWS / Terraform',
        },
        {
          kind: 'Platform',
          title: 'Banking identity hardening',
          description:
            'Provider-based authentication integrating external identity providers, hardening the identity surface of a regulated digital banking platform serving 17M+ end users.',
          stack: 'VueJS / .NET Core / SQL Server',
        },
      ],
    };
  },
  methods: {
    pad(n: number): string {
      return String(n).padStart(2, '0');
    },
    fit() {
      const w = window.innerWidth;
      this.heroPixel = w < 480 ? 5 : w < 820 ? 7 : 9;
    },
  },
  mounted() {
    this.fit();
    window.addEventListener('resize', this.fit);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.fit);
  },
});
</script>

<style scoped>
.hero {
  padding-block: clamp(1.5rem, 4vw, 3rem);
  margin-block-end: clamp(1.5rem, 3vw, 2.5rem);
}

.hero-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
  gap: clamp(1.5rem, 4vw, 4rem);
  align-items: start;
}

@media (max-width: 900px) {
  .hero-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.operator {
  padding: clamp(0.9rem, 1.6vw, 1.25rem);
  border: 1px solid rgba(255, 153, 0, 0.16);
  background: rgba(0, 0, 0, 0.45);
}

.operator dl {
  margin-block: 0.75rem;
}

.operator-status {
  padding-block-start: 0.75rem;
  border-top: 1px solid var(--line-soft);
  width: 100%;
}

.hero-mark {
  margin-block-end: clamp(1.25rem, 2.5vw, 2rem);
}

.hero-headline {
  max-width: 20ch;
  margin-block-end: 1rem;
}

.hero-lede {
  margin-block-end: 2rem;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-block-end: 2.5rem;
}

.hero-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.5rem;
  padding-block-start: 1.25rem;
  border-top: 1px solid var(--line-soft);
}

.hero-readouts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  margin-block-start: 1px;
  background: var(--line-soft);
  border: 1px solid var(--line-soft);
  border-top: 0;
}

.hero-readouts > * {
  padding: clamp(1rem, 2vw, 1.5rem);
  background: #050505;
}

@media (max-width: 760px) {
  .hero-readouts {
    grid-template-columns: minmax(0, 1fr);
  }
}

.section-title {
  margin-block: 0.75rem 1.5rem;
}

.section-body {
  margin-block-start: 0.75rem;
}

.section-body h2 {
  margin-block-end: 1rem;
}

.service {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: clamp(1rem, 2vw, 1.5rem);
  transition: background-color 0.2s linear;
}

.service:hover {
  background: rgba(255, 153, 0, 0.04);
}

.stack-line {
  margin-block-start: auto;
  padding-block-start: 0.75rem;
  letter-spacing: 0.1em;
}

.work-link {
  align-self: flex-start;
  color: var(--nerv-orange);
  font-family: var(--font-nerv-mono);
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  border-bottom: 1px solid rgba(255, 153, 0, 0.35);
  transition: border-color 0.15s linear, text-shadow 0.15s linear;
}

.work-link::after {
  content: ' \2197';
}

.work-link:hover {
  border-bottom-color: var(--nerv-orange);
  text-shadow: 0 0 8px rgba(255, 153, 0, 0.5);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  list-style: none;
}

.chip {
  padding: 0.4rem 0.75rem;
  border: 1px solid rgba(255, 153, 0, 0.25);
  background: rgba(255, 153, 0, 0.05);
  color: var(--nerv-orange);
  font-family: var(--font-nerv-mono);
  font-size: 0.72rem;
  letter-spacing: 0.08em;
}

.cta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
}

.cta h2 {
  margin-block-end: 0.75rem;
}

.proof-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem 1.5rem;
  margin-block-start: 0.75rem;
  padding: 0.9rem clamp(1rem, 2vw, 1.5rem);
  border: 1px solid var(--line-soft);
  border-top: 0;
}

.proof-strip ul {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1.25rem;
  list-style: none;
}

.proof-strip a {
  color: var(--nerv-orange);
  font-family: var(--font-nerv-mono);
  font-size: 0.72rem;
  letter-spacing: 0.06em;
  border-bottom: 1px solid transparent;
  transition: border-color 0.15s linear;
}

.proof-strip a:hover {
  border-bottom-color: var(--nerv-orange);
}
</style>
